/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import {
  Typography,
  Input,
  Form,
  Select,
  Button,
  useToast,
  Checkbox,
  Alert,
  AlertDescription,
} from '@crisellevaldez/speculo-ui';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, PlusCircle, Check, X, Info } from 'lucide-react';
import { IconPill } from '../../common/IconPill';
import { FormattedTextarea } from '../../common/FormattedTextarea';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { buildTransferLineOptions } from '../../../utils/flows/formatTransferNumbers';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { AddTeamMemberModal } from './AddTeamMemberModal';
import {
  IntegrationSetupStep,
  IntegrationSetupSummary,
  IntegrationSetupData,
} from './IntegrationSetupStep';
import { Tooltip } from '../../common/Tooltip';
import { ImproveWithAIButton } from './ImproveWithAIButton';

// Re-export IntegrationSetupData for backwards compatibility
export type { IntegrationSetupData };

interface ConvertFlowStepsProps {
  step: number;
  // Flow basic info
  flowName: string;
  flowSource: string;
  selectedDescription: string;
  customDescription: string;
  transferLines: string[];
  leadEmail?: string;
  useRoundRobin?: boolean;
  // Errors
  flowNameError: string;
  flowSourceError: string;
  descriptionError: string;
  // Basic info handlers
  onFlowNameChange: (value: string) => void;
  onFlowSourceChange: (value: string) => void;
  onSelectedDescriptionChange: (value: string) => void;
  onCustomDescriptionChange: (value: string) => void;
  onTransferLinesChange: (value: string[]) => void;
  onUseRoundRobinChange?: (value: boolean) => void;
  // Integration Setup (for orgs with no existing integration)
  hasIntegration?: boolean;
  integrationSetup?: IntegrationSetupData;
  onIntegrationSetupChange?: (data: IntegrationSetupData) => void;
}

interface User {
  value: string;
  label: string;
}

export const ConvertFlowSteps: React.FC<ConvertFlowStepsProps> = ({
  step,
  // Flow basic info
  flowName,
  flowSource,
  selectedDescription,
  customDescription,
  transferLines,
  leadEmail,
  useRoundRobin,
  // Errors
  flowNameError,
  flowSourceError,
  descriptionError,
  // Basic info handlers
  onFlowNameChange,
  onFlowSourceChange,
  onSelectedDescriptionChange,
  onCustomDescriptionChange,
  onTransferLinesChange,
  onUseRoundRobinChange,
  // Integration Setup (for orgs with no existing integration)
  hasIntegration = true,
  integrationSetup,
  onIntegrationSetupChange,
}) => {
  const { users, loading: loadingUsers, addTeamMember } = useOrganizationUsers();
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { organization } = useOrganizationStore();

  // Helper function to render the summary step
  const renderSummaryStep = () => (
    <div className="space-y-8">
      <div className="bg-white dark:bg-transparent rounded-lg">
        <div className="flex flex-col justify-center items-center gap-2 mb-5">
          <Typography variant="h5" className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Flow Summary
          </Typography>
          <p className="text-red-600 text-sm">
            Please review your flow settings below. Click "Previous" if you need to make any
            changes.
          </p>
        </div>

        <div className="space-y-6">
          {/* Lead Email Card */}
          <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
            <div className="flex items-center gap-4 mb-6">
              <IconPill icon={FileText} size="lg" />
              <Typography
                variant="h6"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                Your Custom Lead Email
              </Typography>
            </div>
            <div>
              <div className="text-gray-800 dark:text-gray-200 text-sm">{leadEmail}</div>
              <Alert variant="info" className="mt-3">
                <AlertDescription>
                  Save this email! When you send your leads to this email they will be added to your
                  Database and launched on this Flow.
                </AlertDescription>
              </Alert>
            </div>
          </div>

          {/* Flow Information Card */}
          <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
            <div className="flex items-center gap-4 mb-6">
              <IconPill icon={FileText} size="lg" />
              <Typography
                variant="h6"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                Flow Information
              </Typography>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Flow Name
                  </div>
                  <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">{flowName}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Lead Source
                  </div>
                  <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                    {flowSource || 'Not specified'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Transfer Line(s)
                  </div>
                  <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                    {transferLines.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {transferLines.length > 0
                          ? buildTransferLineOptions(users)
                              .filter((opt) => transferLines.includes(opt.value))
                              .map((opt) => opt.label)
                              .join(', ')
                          : 'None selected'}
                      </div>
                    ) : (
                      'No transfer lines selected'
                    )}
                  </div>
                </div>
              </div>
              {transferLines.length > 1 && (
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Round Robin
                  </div>
                  <div className="mt-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        useRoundRobin
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-gray-200 text-gray-800 dark:bg-white/10 dark:text-gray-300'
                      }`}
                    >
                      {useRoundRobin ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {useRoundRobin ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                  Description
                </div>
                <div className="mt-2 text-sm text-gray-700 dark:text-gray-400 whitespace-pre-wrap rounded border border-gray-100 dark:border-white/10">
                  {customDescription || 'No description provided'}
                </div>
              </div>
            </div>
          </div>

          {/* Integration Setup Info (for orgs with no existing integration) */}
          {!hasIntegration && integrationSetup && integrationSetup.contactType && (
            <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
              <div className="flex items-center gap-4 mb-6">
                <IconPill icon={FileText} size="lg" />
                <Typography
                  variant="h6"
                  className="text-base font-semibold text-gray-900 dark:text-gray-100"
                >
                  Integration Setup
                </Typography>
              </div>
              <IntegrationSetupSummary integrationSetup={integrationSetup} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            <Form.Control error={flowNameError}>
              <Form.Label>What is the name of this Convert Flow?</Form.Label>
              <Input
                placeholder="Enter flow name"
                value={flowName}
                onChange={(e) => onFlowNameChange(e.target.value)}
                required
                helperText="This is the internal name. Make sure it is something that is unique to this flow."
              />
            </Form.Control>

            <Form.Control error={flowSourceError}>
              <Form.Label>Lead Source</Form.Label>
              <Input
                helperText="Where is this lead originating?"
                value={flowSource}
                onChange={(e) => onFlowSourceChange(e.target.value)}
                placeholder="Enter lead source (e.g., Google PPC, Facebook Ads, Zillow)"
              />
            </Form.Control>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <Form.Control error={descriptionError}>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                <Form.Label className="mb-0">Describe how this Lead was Obtained</Form.Label>
                <Tooltip
                  content="Edit the description directly or click 'Improve Description' to enhance your description and make it more professional and clear."
                  position="right"
                >
                  <span className="group rounded-full transition-colors cursor-help flex items-center">
                    <Info className="w-4 h-4 text-gray-400 group-hover:text-yellow-600" />
                  </span>
                </Tooltip>
                <ImproveWithAIButton
                  text={customDescription}
                  onImproved={onCustomDescriptionChange}
                />
              </div>
              <div className="space-y-2">
                <FormattedTextarea
                  value={customDescription}
                  onChange={(value) => onCustomDescriptionChange(value)}
                  placeholder="We are calling people who just requested more information on a property on our website"
                  required
                />
                <p className="text-muted-foreground text-sm">
                  Be as detailed as possible as the AI will use this as its knowledge to have good
                  conversations. How was the lead obtained? Did the contact register on the website?
                </p>
              </div>
            </Form.Control>
          </div>
        );

      case 2:
        return (
          <div className="space-y-2">
            <Form.Control>
              <Form.Label>Transfer Line(s)</Form.Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 min-w-0">
                  <Select
                    className="min-h-9"
                    isLoading={loadingUsers}
                    options={buildTransferLineOptions(users)}
                    rows={4}
                    value={transferLines}
                    onChange={(value) => {
                      if (Array.isArray(value)) {
                        onTransferLinesChange(value);
                        // Reset useRoundRobin to false if less than 2 transfer lines
                        if (value.length < 2 && useRoundRobin && onUseRoundRobinChange) {
                          onUseRoundRobinChange(false);
                        }
                      }
                    }}
                    placeholder="Select team members"
                    multiple={true}
                    searchable
                  />
                </div>
                <Button
                  variant="outline"
                  className="whitespace-nowrap h-9 w-full sm:w-auto justify-center"
                  onClick={() => setShowAddTeamMember(true)}
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add New Team
                </Button>
              </div>
            </Form.Control>

            {/* Use Round Robin Checkbox */}
            {transferLines.length > 1 && (
              <Form.Control className="flex items-center">
                <div className="flex mt-2 gap-2">
                  <Checkbox
                    id="useRoundRobin"
                    checked={useRoundRobin || false}
                    onChange={(e) =>
                      onUseRoundRobinChange && onUseRoundRobinChange(e.target.checked)
                    }
                    label="Enable Round Robin for this Flow"
                  />
                </div>
              </Form.Control>
            )}

            <AddTeamMemberModal
              isOpen={showAddTeamMember}
              onClose={() => setShowAddTeamMember(false)}
              onSubmit={async (data) => {
                setIsAddingTeamMember(true);
                try {
                  if (!organization?.id) throw new Error('No organization ID');
                  await addTeamMember(organization.id, data);
                  await queryClient.invalidateQueries({ queryKey: ['organizationUsers'] });
                  setShowAddTeamMember(false);
                  showToast({ message: 'Team member invited successfully', variant: 'success' });
                } catch (error) {
                  showToast({
                    message: error instanceof Error ? error.message : 'Error inviting team member',
                    variant: 'error',
                  });
                } finally {
                  setIsAddingTeamMember(false);
                }
              }}
              isSubmitting={isAddingTeamMember}
            />
          </div>
        );

      // Integration Setup step (only for orgs with no existing integration)
      case 3:
        // Only render if no integration exists and integration setup props are provided
        if (hasIntegration || !integrationSetup || !onIntegrationSetupChange) {
          return renderSummaryStep();
        }
        return (
          <IntegrationSetupStep
            integrationSetup={integrationSetup}
            onIntegrationSetupChange={onIntegrationSetupChange}
            flowName={flowName}
          />
        );

      // Summary step
      case 4:
        return renderSummaryStep();

      default:
        return null;
    }
  };

  return <div className="py-6">{renderStep()}</div>;
};
